import { useTranslation } from 'react-i18next';
import { coinsToGel } from './coinValue.js';
import { Button, Heading, Icon, Muted, ProgressBar, Skeleton, Stack, Text } from '@fluent/ui';
import { isDone, missionsByTier, type Mission, type MissionsState, type MissionTier } from './hooks.js';
import { CoinGlyph } from '../profile/icons.js';
import { EVERYTHING_FREE } from '../subscription/freeMode.js';
import '../screens/Missions.css';

/**
 * MissionsSections — the missions half of the ჯილდოები-tab hub (gold
 * balance hero + daily/weekly/monthly ladders). Presentational: the
 * combined Achievements page and /missions-review both mount it. All
 * numbers come from the server's refresh_missions recompute — coins are
 * money, the client reports nothing (same trust rule as /subscription).
 */
export interface MissionsSectionsProps {
  state: MissionsState | null;
  loading: boolean;
  failed?: boolean;
  onRetry: () => void;
}

export function MissionsSections({ state, loading, failed, onRetry }: MissionsSectionsProps) {
  const { t, i18n } = useTranslation();
  const numberLocale = i18n.language.startsWith('ka') ? 'ka-GE' : 'en-US';
  const tiers: MissionTier[] = ['daily', 'weekly', 'monthly'];

  if (loading) {
    return (
      <section className="fluent-missions__hero" aria-busy="true">
        {/* mirrors the loaded hero: coin, balance, bar, cap line, 2-line explainer */}
        <Skeleton width={72} height={72} />
        <Skeleton width="50%" height={34} />
        <Skeleton shape="block" width="100%" height={12} />
        <Skeleton width="35%" height={14} />
        <Skeleton width="85%" height={14} />
        <Skeleton width="60%" height={14} />
      </section>
    );
  }

  if (failed || !state) {
    return (
      <section className="fluent-missions__hero">
        <Stack gap={3} align="center" style={{ textAlign: 'center' }}>
          <Text tone="muted">{t('missions.error_body')}</Text>
          <Button variant="secondary" size="md" onClick={onRetry}>
            {t('missions.retry')}
          </Button>
        </Stack>
      </section>
    );
  }

  return (
    <>
      {/* ---------- balance hero ---------- */}
      <section className="fluent-missions__hero">
        <span className="fluent-missions__coin" aria-hidden="true">
          <Icon size={36}>
            <CoinGlyph />
          </Icon>
        </span>
        <div className="fluent-missions__balance">
          {state.balance.toLocaleString(numberLocale)}
          <span className="fluent-missions__balance-label"> {t('missions.balance_label')}</span>
        </div>
        {/* ONE cap both sides (earn = spend = 400): the bar is the whole
            monthly story, and everything earned is usable. */}
        <div className="fluent-missions__cap">
          <ProgressBar
            value={state.month_cap > 0 ? Math.min(state.month_awarded / state.month_cap, 1) : 0}
            tone="xp"
            size="md"
            label={t('missions.month_line', { awarded: state.month_awarded, cap: state.month_cap })}
          />
          <Text tone="muted" size="xs" weight="bold">
            {state.month_awarded >= state.month_cap
              ? t('missions.month_full')
              : t('missions.month_line', { awarded: state.month_awarded, cap: state.month_cap })}
          </Text>
        </div>
        <Muted className="fluent-missions__explainer">
          {/* While everything is free (freeMode.ts) coins discount nothing. */}
          {EVERYTHING_FREE
            ? t('missions.explainer_free', { cap: state.month_cap })
            : t('missions.explainer', { cap: state.month_cap, gel: coinsToGel(state.month_cap) })}
        </Muted>
      </section>

      {/* ---------- tier ladders ---------- */}
      {tiers.map((tier) => {
        const list = missionsByTier(state, tier);
        if (list.length === 0) return null;
        return (
          <section key={tier} className="fluent-missions__section">
            <Heading level={3} as="h2">
              {t(`missions.tier.${tier}`)}
            </Heading>
            <div className="fluent-missions__list">
              {list.map((m) => (
                <MissionRow
                  key={m.id}
                  mission={m}
                  atWall={state.month_awarded >= state.month_cap}
                />
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

function MissionRow({ mission, atWall }: { mission: Mission; atWall: boolean }) {
  const { t } = useTranslation();
  const done = isDone(mission);
  const progress = Math.min(mission.progress, mission.target);

  return (
    <div className="fluent-missions__row" data-done={done || undefined}>
      <span className="fluent-missions__row-check" aria-hidden="true">
        {done ? '✓' : ''}
      </span>
      <div className="fluent-missions__row-main">
        <div className="fluent-missions__row-top">
          <Text weight="bold" size="sm">
            {t(`missions.m.${mission.id}.title`)}
          </Text>
          {/* at the monthly wall, pending chips grey out — no gold promise
              the month can't keep */}
          <span
            className="fluent-missions__row-coins"
            data-done={done || undefined}
            data-capped={(!done && atWall) || undefined}
          >
            <Icon size={12}>
              <CoinGlyph />
            </Icon>
            {t('missions.coins_chip', {
              // done chips show what the ledger ACTUALLY paid — the cap can
              // clip an award, and money UI never advertises more than paid
              count: done ? (mission.awarded ?? 0) : mission.coins,
            })}
          </span>
        </div>
        <Text tone="muted" size="xs" style={{ display: 'block' }}>
          {t(`missions.m.${mission.id}.desc`)}
        </Text>
        <div className="fluent-missions__row-bar">
          <ProgressBar
            value={mission.target > 0 ? progress / mission.target : 0}
            tone={done ? 'primary' : 'xp'}
            size="sm"
            label={t('missions.progress_aria', { progress, target: mission.target })}
          />
          <span className="fluent-missions__row-count">
            {progress}/{mission.target}
          </span>
        </div>
      </div>
    </div>
  );
}
